import type { SeoScoreContext } from '../context';
import { clip, fail, maxStatus, pass, pct, rangeStatus, warn, type SeoRule } from '../rule';
import { T } from '../thresholds';
import { countSyllablesFr, fleschFr, normalizeFr, sentenceTokens } from '../tokenize-fr';
import { SeoRuleKey, SeoScoreGroup } from '../types';
import { FR_PASSIVE_AUX, FR_STOPWORDS, FR_TRANSITION_WORDS } from '../wordlists-fr';

const G = SeoScoreGroup.READABILITY;

const ADVERBS_BETWEEN = new Set([
  'pas',
  'plus',
  'jamais',
  'deja',
  'tres',
  'bien',
  'souvent',
  'egalement',
  'aussi',
  'toujours',
  'encore',
  'generalement',
  'parfois',
  'rarement',
  'desormais',
  'alors',
  'ensuite',
  'donc',
]);

/** Phrase au passif (heuristique) : auxiliaire être + participe passé (+ « par »). */
export function isPassiveSentence(sentence: string): boolean {
  const lowered = sentence.toLowerCase().replace(/[’‘ʼ]/g, "'");
  const tokens = lowered.match(/[a-zà-ÿ]+/g) ?? [];
  for (let i = 0; i < tokens.length - 1; i++) {
    if (!FR_PASSIVE_AUX.has(normalizeFr(tokens[i]))) continue;
    let j = i + 1;
    while (j < tokens.length && ADVERBS_BETWEEN.has(normalizeFr(tokens[j]))) j++;
    const next = tokens[j];
    if (!next || next.length < 4 || FR_STOPWORDS.has(normalizeFr(next))) continue;
    if (/(é|ée|és|ées)$/.test(next)) return true;
    if (/(i|is|ie|ies|u|ue|us|ues|it|ite|its|ites)$/.test(next)) {
      const window = tokens.slice(j + 1, j + 7).map((t): string => normalizeFr(t));
      if (window.includes('par')) return true;
    }
  }
  return false;
}

const TRANSITIONS = FR_TRANSITION_WORDS.map((t): string =>
  normalizeFr(t).replace(/'/g, ' ').replace(/\s+/g, ' ').trim(),
);

export function hasTransition(sentence: string): boolean {
  const norm = ` ${normalizeFr(sentence)
    .replace(/'/g, ' ')
    .replace(/[^a-z0-9 ]+/g, ' ')
    .replace(/\s+/g, ' ')} `;
  return TRANSITIONS.some((t): boolean => norm.includes(` ${t} `));
}

function nominalStackDepth(sentence: string): number {
  const norm = normalizeFr(sentence).replace(/'/g, ' ');
  let max = 0;
  for (const clause of norm.split(/[,;:.!?()]/)) {
    const count = (clause.match(/\b(de|du|des|d)\b/g) ?? []).length;
    if (count > max) max = count;
  }
  return max;
}

export interface ReadabilityStats {
  sentences: number;
  words: number;
  avgSentenceLength: number;
  longSentenceRatio: number;
  readability: number;
  passiveRatio: number;
  transitionRatio: number;
}

const READABILITY_CACHE = new WeakMap<SeoScoreContext, ReadabilityStats>();

export function computeReadability(ctx: SeoScoreContext): ReadabilityStats {
  const cached = READABILITY_CACHE.get(ctx);
  if (cached) return cached;
  const computed = computeReadabilityUncached(ctx);
  READABILITY_CACHE.set(ctx, computed);
  return computed;
}

function computeReadabilityUncached(ctx: SeoScoreContext): ReadabilityStats {
  const sentences = ctx.sentences;
  let words = 0;
  let syllables = 0;
  let long = 0;
  let passive = 0;
  let transition = 0;
  for (const s of sentences) {
    const rawWords = s.match(/[A-Za-zÀ-ÿ0-9]+/g) ?? [];
    words += rawWords.length;
    for (const w of rawWords) syllables += countSyllablesFr(w);
    if (rawWords.length > T.LONG_SENTENCE_WORDS) long++;
    if (isPassiveSentence(s)) passive++;
    if (hasTransition(s)) transition++;
  }
  const n = sentences.length;
  return {
    sentences: n,
    words,
    avgSentenceLength: n === 0 ? 0 : words / n,
    longSentenceRatio: n === 0 ? 0 : long / n,
    readability: fleschFr(words, n, syllables),
    passiveRatio: n === 0 ? 0 : passive / n,
    transitionRatio: n === 0 ? 0 : transition / n,
  };
}

const MIN_SENTENCES = 5;

export const READABILITY_RULES: SeoRule[] = [
  {
    key: SeoRuleKey.SENTENCE_LENGTH_AVG,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      const r = computeReadability(ctx);
      if (r.sentences < MIN_SENTENCES) return null;
      return maxStatus(
        r.avgSentenceLength,
        T.SENTENCE_AVG.PASS,
        T.SENTENCE_AVG.WARN,
        (v): string => `${Math.round(v)} mots / phrase`,
      );
    },
  },
  {
    key: SeoRuleKey.LONG_SENTENCES,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      const r = computeReadability(ctx);
      if (r.sentences < MIN_SENTENCES) return null;
      const long = ctx.sentences.filter(
        (s): boolean => (s.match(/[A-Za-zÀ-ÿ0-9]+/g) ?? []).length > T.LONG_SENTENCE_WORDS,
      );
      return maxStatus(
        r.longSentenceRatio,
        T.LONG_SENTENCES_RATIO.PASS,
        T.LONG_SENTENCES_RATIO.WARN,
        (v): string => `${pct(v)} de phrases > ${T.LONG_SENTENCE_WORDS} mots`,
        long.slice(0, 5).map((s): string => clip(s)),
      );
    },
  },
  {
    key: SeoRuleKey.READABILITY_FR,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      const r = computeReadability(ctx);
      if (r.sentences < MIN_SENTENCES) return null;
      return rangeStatus(r.readability, T.READABILITY.PASS, T.READABILITY.WARN, (v): string => `${v} / 100`);
    },
  },
  {
    key: SeoRuleKey.PASSIVE_VOICE,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      const r = computeReadability(ctx);
      if (r.sentences < MIN_SENTENCES) return null;
      const locations = ctx.sentences
        .filter((s): boolean => isPassiveSentence(s))
        .slice(0, 5)
        .map((s): string => clip(s));
      return maxStatus(r.passiveRatio, T.PASSIVE_RATIO.PASS, T.PASSIVE_RATIO.WARN, (v): string => pct(v), locations);
    },
  },
  {
    key: SeoRuleKey.TRANSITION_WORDS,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      const r = computeReadability(ctx);
      if (r.sentences < MIN_SENTENCES) return null;
      // Plafond : trop de connecteurs = tic de rédaction automatique (2026.09.3).
      return maxStatus(r.transitionRatio, T.TRANSITION_RATIO.PASS_MAX, T.TRANSITION_RATIO.WARN_MAX, (v): string =>
        pct(v),
      );
    },
  },
  {
    key: SeoRuleKey.SAME_SENTENCE_START,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      if (ctx.sentences.length < MIN_SENTENCES) return null;
      let run = 1;
      let runs = 0;
      const locations: string[] = [];
      let previous = '';
      for (const s of ctx.sentences) {
        const first = sentenceTokens(s)[0] ?? '';
        if (first.length > 0 && first === previous) {
          run++;
          if (run === T.SAME_START_RUN) {
            runs++;
            locations.push(clip(s));
          }
        } else run = 1;
        previous = first;
      }
      if (runs === 0) return pass(0, '0 série');
      if (runs === 1) return warn(1, '0 série', locations);
      return fail(runs, '0 série', locations);
    },
  },
  {
    key: SeoRuleKey.NOMINAL_STACKS,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      if (ctx.sentences.length < MIN_SENTENCES) return null;
      const stacked = ctx.sentences.filter((s): boolean => nominalStackDepth(s) >= T.NOMINAL_STACK_DEPTH);
      const locations = stacked.slice(0, 5).map((s): string => clip(s));
      if (stacked.length === 0) return pass(0, '0');
      if (stacked.length <= 2) return warn(stacked.length, '0', locations);
      return fail(stacked.length, '0', locations);
    },
  },
];
